<template>
  <BaseWrapper>
    <BaseCard>
      <div
        class="md:flex md:items-start md:justify-between"
        data-test="start-from-selection"
      >
        <div class="md:w-2/3">
          <h2 class="text-2xl">
            Start from your selection
          </h2>
          <p class="mt-2 text-gray-600">
            <span v-if="template">You chose <strong>{{ template.name }}</strong> on our website.</span>
            <span v-else>You chose tests on our website.</span>
            Adjust the tests, name the assessment, then invite your first candidate.
          </p>
          <p
            v-if="template && template.summary"
            class="mt-2 text-sm text-gray-600"
          >
            {{ template.summary }}
          </p>
        </div>
        <div class="mt-4 md:mt-0 md:text-right">
          <button
            type="button"
            class="text-sm underline text-gray-600 hover:text-gray-800 focus:outline-none focus:shadow-outline"
            @click="dismiss"
          >
            Start with a blank assessment instead
          </button>
        </div>
      </div>

      <div
        v-if="state === 'loading'"
        class="mt-6"
      >
        <Loader />
        <p class="mt-2 text-sm text-center text-gray-600">
          Checking which tests are available
        </p>
      </div>

      <div
        v-else-if="state === 'error'"
        class="mt-6"
      >
        <p class="text-red-600">
          We could not load the test catalogue just now.
        </p>
        <BaseButton
          class="mt-3"
          variant="salmon"
          @click="load"
        >
          Try again
        </BaseButton>
      </div>

      <div
        v-else-if="state === 'empty'"
        class="mt-6"
      >
        <p class="text-gray-700">
          The tests in that selection are not available in your library right now, so nothing was set up automatically.
        </p>
        <CreateAssessmentButton class="mt-4" />
      </div>

      <form
        v-else
        class="mt-6"
        novalidate
        @submit.prevent="submit"
      >
        <fieldset>
          <legend class="text-sm font-medium text-gray-800">
            Tests in this assessment
          </legend>
          <ul class="mt-2 space-y-2">
            <li
              v-for="exam in proposed"
              :key="exam.slug"
              class="flex items-start"
            >
              <label class="flex items-start cursor-pointer">
                <input
                  v-model="selectedSlugs"
                  type="checkbox"
                  class="mt-1 form-checkbox"
                  :value="exam.slug"
                  :aria-describedby="`exam-meta-${exam.slug}`"
                >
                <span class="ml-2">
                  <span class="font-medium text-gray-800">{{ exam.name }}</span>
                  <span
                    :id="`exam-meta-${exam.slug}`"
                    class="block text-sm text-gray-600"
                  >
                    <span v-if="exam.minutes">{{ exam.minutes }} minutes, timed</span>
                    <span v-else>Not timed</span>
                    <span v-if="isLocked(exam)"> · needs a plan</span>
                  </span>
                </span>
              </label>
            </li>
          </ul>
          <p
            class="mt-3 text-sm text-gray-600"
            data-test="battery-duration"
          >
            <span v-if="selectedExams.length === 0">No tests selected.</span>
            <span v-else-if="untimedCount === 0">{{ selectedExams.length }} {{ selectedExams.length === 1 ? 'test' : 'tests' }}, about {{ minutes }} minutes for a candidate.</span>
            <span v-else>{{ selectedExams.length }} {{ selectedExams.length === 1 ? 'test' : 'tests' }}, about {{ minutes }} minutes of timed tests plus {{ untimedCount }} untimed.</span>
          </p>
          <p
            v-if="lockedSelected.length"
            class="mt-2 text-sm text-yellow-800"
            data-test="locked-notice"
          >
            {{ lockedSelected.map(exam => exam.name).join(', ') }} {{ lockedSelected.length === 1 ? 'is' : 'are' }} not included without a plan now that your trial has ended.
            <router-link
              :to="{ name: 'settings-payg-plan' }"
              class="underline"
            >
              Choose a plan
            </router-link>
            or untick {{ lockedSelected.length === 1 ? 'it' : 'them' }}.
          </p>
        </fieldset>

        <label class="block mt-6 max-w-md">
          <span class="text-sm text-gray-800">Name of your assessment</span>
          <input
            v-model="name"
            type="text"
            class="w-full mt-1 form-input"
            maxlength="255"
            data-test="assessment-name"
          >
        </label>

        <p
          v-if="isPerCandidate"
          class="mt-4 text-sm text-gray-600"
          data-test="pricing-note"
        >
          <span v-if="candidateBilling">Results for your first {{ candidateBilling.free_candidates }} candidates are free, and every test is available.</span>
          <span v-else>Every test is available.</span>
          Add a card from
          <router-link
            :to="{ name: 'settings-payg-plan' }"
            class="underline"
          >
            Settings, Plan
          </router-link>
          when you want more.
        </p>
        <p
          v-else-if="intent && intent.plan"
          class="mt-4 text-sm text-gray-600"
          data-test="plan-preference"
        >
          You looked at the <strong>{{ planName(intent.plan) }}</strong> plan. Your trial starts without a card; choose a plan from
          <router-link
            :to="{ name: 'settings-payg-plan' }"
            class="underline"
          >
            Settings, Plan
          </router-link>
          when you are ready.
        </p>

        <div class="mt-6 flex items-center space-x-4">
          <BaseButton
            :loading="saving"
            :disabled="!canCreate"
            @click="submit"
          >
            Create assessment
          </BaseButton>
          <span
            v-if="errorMessage"
            class="text-sm text-red-600"
            role="alert"
          >
            {{ errorMessage }}
          </span>
        </div>
      </form>
    </BaseCard>
  </BaseWrapper>
</template>

<script>
import CreateAssessmentButton from '@components/ClientAssessments/CreateAssessmentButton'
import Loader from '@components/Loader'

import { clearIntent, proposedExams, totalMinutes } from '@utils/signupIntent'
import { mapGetters } from 'vuex'

const PLAN_NAMES = { starter: 'Starter', essential: 'Essential', standard: 'Business' }

export default {
  components: {
    CreateAssessmentButton,
    Loader
  },

  props: {
    intent: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      loading: true,
      loadError: null,
      saving: false,
      errorMessage: null,
      name: '',
      selectedSlugs: [],
      nameTouched: false
    }
  },

  computed: {
    ...mapGetters({
      templateBySlug: 'pilots/templateBySlug',
      haveTemplatesLoaded: 'pilots/haveTemplatesLoaded',
      catalogue: 'clientExams/exams',
      inTrial: 'organisations/inTrial',
      isPerCandidate: 'organisations/isPerCandidate',
      candidateBilling: 'subscriptions/candidateBilling',
      hasAnySubscription: 'subscriptions/hasAnySubscription',
      hasPaygUnlockedExams: 'organisations/hasPaygUnlockedExams',
      hasUnlimitedJobs: 'organisations/hasUnlimitedJobs',
      maxExams: 'pilots/maxExams'
    }),

    /**
     * @return {Object|null}
     */
    template() {
      if (!this.intent || !this.intent.template) {
        return null
      }
      return this.templateBySlug(this.intent.template)
    },

    /**
     * @return {Array}
     */
    proposed() {
      return proposedExams(this.intent, this.template, this.catalogue || [])
    },

    /**
     * @return {Array}
     */
    selectedExams() {
      return this.proposed.filter(exam => this.selectedSlugs.includes(exam.slug))
    },

    /**
     * @return {Array}
     */
    lockedSelected() {
      return this.selectedExams.filter(exam => this.isLocked(exam))
    },

    /**
     * @return {Number}
     */
    minutes() {
      return totalMinutes(this.selectedExams)
    },

    /**
     * @return {Number}
     */
    untimedCount() {
      return this.selectedExams.filter(exam => !exam.minutes).length
    },

    /**
     * @return {string}
     */
    state() {
      if (this.loadError) {
        return 'error'
      }
      if (this.loading) {
        return 'loading'
      }
      if (this.proposed.length === 0) {
        return 'empty'
      }
      return 'ready'
    },

    /**
     * @return {Boolean}
     */
    canCreate() {
      return !this.saving &&
        this.name.trim().length > 0 &&
        this.selectedExams.length > 0 &&
        this.selectedExams.length <= this.maxExams &&
        this.lockedSelected.length === 0
    }
  },

  watch: {
    proposed: {
      immediate: true,
      handler(exams) {
        // Only pre-select on first load so a user's unticking is kept.
        if (this.selectedSlugs.length === 0 && exams.length) {
          this.selectedSlugs = exams.map(exam => exam.slug)
        }
        if (!this.nameTouched) {
          this.name = this.defaultName()
        }
      }
    },
    template() {
      if (!this.nameTouched) {
        this.name = this.defaultName()
      }
    }
  },

  created() {
    this.load()
  },

  methods: {
    /**
     * Mirrors the library's own rule for adding a non-free test
     *
     * @param {Object} exam
     * @return {Boolean}
     */
    isLocked(exam) {
      if (exam.includeInFreePlan || this.hasUnlimitedJobs || this.hasPaygUnlockedExams || this.inTrial || this.hasAnySubscription) {
        return false
      }
      return true
    },

    /**
     * @param {string} slug
     * @return {string}
     */
    planName(slug) {
      return PLAN_NAMES[slug] || slug
    },

    /**
     * @return {string}
     */
    defaultName() {
      if (this.template) {
        return this.template.assessmentName || this.template.name
      }
      if (this.proposed.length) {
        return `${this.proposed[0].name} assessment`
      }
      return ''
    },

    load() {
      this.loading = true
      this.loadError = null
      const work = []
      if (this.intent && this.intent.template) {
        work.push(this.$store.dispatch('pilots/getTemplates'))
      }
      if (this.intent && this.intent.tests && this.intent.tests.length && (!this.catalogue || this.catalogue.length === 0)) {
        work.push(this.$store.dispatch('clientExams/getClientExams'))
      }
      Promise.all(work)
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.loadError = error
        })
    },

    dismiss() {
      clearIntent(window.localStorage)
      this.$store.dispatch('pilots/setIntent', null)
      this.$emit('dismiss')
    },

    submit() {
      if (!this.canCreate) {
        return
      }
      this.saving = true
      this.errorMessage = null

      const job = {
        name: this.name.trim(),
        exams: this.selectedExams.map(exam => exam.slug)
      }
      if (this.template) {
        job.templateSlug = this.template.slug
      }

      this.$store.dispatch('jobs/create', job)
        .then(created => {
          clearIntent(window.localStorage)
          this.$store.dispatch('pilots/setIntent', null)
          this.$emit('created', created)
          this.$router.push({ name: 'client-assessments-show', params: { id: created.uuid } })
        })
        .catch(error => {
          this.saving = false
          if (error.response && error.response.data && error.response.data.errors) {
            const errors = error.response.data.errors
            this.errorMessage = Array.isArray(errors) ? errors[0] : 'Please check the assessment name and try again.'
            return
          }
          this.errorMessage = 'We could not create the assessment. Check your connection and try again; nothing has been lost.'
        })
    }
  }
}
</script>
